import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * @typedef {Object} WalloffameRefs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/**
 * @typedef {Object} DragState
 * @property {number} target
 * @property {number} start
 * @property {number} max
 * @property {number} activeSize - The resolved pixel size of the active panel at drag start
 * @property {boolean} left
 * @property {boolean} [dragging]
 * @property {boolean} [prevent]
 * @property {number} [progress]
 * @property {number} [lastClientX]
 */

const DRAG_THRESHOLD = 3;
const DRAG_COMPLETE_THRESHOLD = 0.15;
const INACTIVE_SIZE = 56; // Px size of inactive tabs on desktop
const INACTIVE_MOBILE_SIZE = 44; // Px size of inactive tabs on mobile
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** @extends {Component<WalloffameRefs>} */
export class WalloffameComponent extends Component {
  requiredRefs = ['container'];
  #active = 0;
  /** @type {DragState | null} */
  #drag = null;
  /** @type {AbortController | null} */
  #abort = null;
  #isMobile = false;
  /** @type {ResizeObserver | null} */
  #heightObserver = null;
  /** @type {MutationObserver | null} */
  #contentObserver = null;
  /** @type {ResizeObserver | null} */
  #containerObserver = null;
  /** @type {number | null} */
  #scrollRafId = null;
  /** @type {number | null} */
  #dragRafId = null;
  /** @type {((e: MouseEvent) => void) | null} */
  #mouseMoveHandler = null;
  /** @type {((e: MouseEvent) => void) | null} */
  #mouseUpHandler = null;

  /** @returns {number} The inactive tab size in pixels based on current viewport */
  get #inactiveSize() {
    return this.#isMobile ? INACTIVE_MOBILE_SIZE : INACTIVE_SIZE;
  }

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#isMobile = isMobileBreakpoint();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    this.#updateActiveTab();
    this.#setupEventListeners();
    this.#observeContentHeight();
    this.#updateProgressBar();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { panels, container } = this.refs;

    this.#setupPanelFocusManagement(opts);

    // Listen for scroll events to update active slide and progress bar
    const panelsContainer = container?.querySelector('.walloffame__panels');
    if (panelsContainer) {
      panelsContainer.addEventListener('scroll', () => this.#handleScroll(), opts);
      
      // Setup drag functionality
      this.#setupDragListeners(panelsContainer, opts);
    }
  }

  /**
   * @param {HTMLElement} panelsContainer
   * @param {AddEventListenerOptions & { signal: AbortSignal }} opts
   */
  #setupDragListeners(panelsContainer, opts) {
    // Touch events for mobile
    panelsContainer.addEventListener('touchstart', (e) => this.#handleDragStart(e, panelsContainer), { ...opts, passive: false });
    panelsContainer.addEventListener('touchmove', (e) => this.#handleDragMove(e, panelsContainer), { ...opts, passive: false });
    panelsContainer.addEventListener('touchend', (e) => this.#handleDragEnd(e, panelsContainer), opts);
    panelsContainer.addEventListener('touchcancel', (e) => this.#handleDragEnd(e, panelsContainer), opts);

    // Mouse events for desktop
    panelsContainer.addEventListener('mousedown', (e) => this.#handleDragStart(e, panelsContainer), opts);
    
    // Global mouse events to handle dragging outside the container
    // These are only active when dragging, managed in handleDragStart/End
    this.#mouseMoveHandler = (e) => this.#handleDragMove(e, panelsContainer);
    this.#mouseUpHandler = (e) => this.#handleDragEnd(e, panelsContainer);
  }

  #handleScroll = () => {
    // Don't update during drag to avoid conflicts
    if (this.#drag?.dragging) return;

    // Throttle scroll updates with requestAnimationFrame to avoid layout thrashing
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateActiveSlideFromScroll();
    });
  };

  #updateActiveSlideFromScroll() {
    const { panels, container } = this.refs;
    if (!panels || !container) return;

    const panelsContainer = container.querySelector('.walloffame__panels');
    if (!panelsContainer) return;

    const scrollLeft = panelsContainer.scrollLeft;
    const containerWidth = panelsContainer.clientWidth;

    // Find which slide is most visible based on scroll position
    let newActive = 0;
    let minDistance = Infinity;

    for (const [i, panel] of panels.entries()) {
      const panelRect = panel.getBoundingClientRect();
      const containerRect = panelsContainer.getBoundingClientRect();
      const panelLeft = panelRect.left - containerRect.left + scrollLeft;
      const panelCenter = panelLeft + panelRect.width / 2;
      const containerCenter = scrollLeft + containerWidth / 2;
      const distance = Math.abs(panelCenter - containerCenter);

      if (distance < minDistance) {
        minDistance = distance;
        newActive = i;
      }
    }

    if (newActive !== this.#active) {
      this.#active = newActive;
      this.#updateProgressBar();
    }
  }

  /**
   * @param {MouseEvent | TouchEvent} event
   * @param {HTMLElement} panelsContainer
   */
  #handleDragStart(event, panelsContainer) {
    // Don't start drag if clicking on interactive elements
    const target = event.target;
    if (target instanceof HTMLElement) {
      const isInteractive = target.closest('a, button, input, select, textarea, [role="button"]');
      if (isInteractive) return;
    }

    const clientX = this.#getClientX(event);
    if (clientX === null) return;

    const scrollLeft = panelsContainer.scrollLeft;
    const maxScroll = panelsContainer.scrollWidth - panelsContainer.clientWidth;

    this.#drag = {
      target: scrollLeft,
      start: clientX,
      max: maxScroll,
      activeSize: panelsContainer.clientWidth,
      left: false,
      dragging: false,
      prevent: false,
      lastClientX: clientX,
    };

    // Add global mouse listeners for desktop drag
    if (event instanceof MouseEvent && this.#mouseMoveHandler && this.#mouseUpHandler) {
      document.addEventListener('mousemove', this.#mouseMoveHandler);
      document.addEventListener('mouseup', this.#mouseUpHandler);
    }

    // Prevent default scrolling behavior
    if (event instanceof TouchEvent) {
      event.preventDefault();
    }
  }

  /**
   * @param {MouseEvent | TouchEvent} event
   * @param {HTMLElement} panelsContainer
   */
  #handleDragMove(event, panelsContainer) {
    if (!this.#drag) return;

    const clientX = this.#getClientX(event);
    if (clientX === null) return;

    const deltaX = this.#drag.start - clientX;
    const absDeltaX = Math.abs(deltaX);

    // Check if we've exceeded the drag threshold
    if (!this.#drag.dragging && absDeltaX > DRAG_THRESHOLD) {
      this.#drag.dragging = true;
      this.#drag.left = deltaX > 0;
      const { container } = this.refs;
      if (container) {
        container.setAttribute('data-dragging', '');
      }
      // Disable scroll snap during drag
      panelsContainer.style.scrollSnapType = 'none';
    }

    if (!this.#drag.dragging) return;

    // Prevent default scrolling during drag
    if (event instanceof TouchEvent) {
      event.preventDefault();
    }

    // Use requestAnimationFrame for smooth updates
    if (this.#dragRafId !== null) {
      cancelAnimationFrame(this.#dragRafId);
    }

    this.#dragRafId = requestAnimationFrame(() => {
      this.#dragRafId = null;
      
      if (!this.#drag) return;

      // Calculate new scroll position directly from mouse/touch movement
      // No artificial limits - let the user drag freely
      const newScrollLeft = this.#drag.target + deltaX;
      
      // Clamp to valid scroll range
      const clampedScroll = Math.max(0, Math.min(this.#drag.max, newScrollLeft));
      
      // Apply scroll position directly for immediate response
      panelsContainer.scrollLeft = clampedScroll;

      // Calculate progress for snap decision (based on panel width)
      const panelWidth = panelsContainer.clientWidth;
      const scrollDelta = clampedScroll - this.#drag.target;
      this.#drag.progress = scrollDelta / panelWidth;
      
      // Update last position for smooth tracking
      this.#drag.lastClientX = clientX;
    });
  }

  /**
   * @param {MouseEvent | TouchEvent} event
   * @param {HTMLElement} panelsContainer
   */
  #handleDragEnd(event, panelsContainer) {
    if (!this.#drag) return;

    // Cancel any pending drag animation frame
    if (this.#dragRafId !== null) {
      cancelAnimationFrame(this.#dragRafId);
      this.#dragRafId = null;
    }

    const wasDragging = this.#drag.dragging;
    const { container } = this.refs;

    // Remove global mouse listeners
    if (this.#mouseMoveHandler && this.#mouseUpHandler) {
      document.removeEventListener('mousemove', this.#mouseMoveHandler);
      document.removeEventListener('mouseup', this.#mouseUpHandler);
    }

    // Re-enable scroll snap
    panelsContainer.style.scrollSnapType = '';

    if (wasDragging && container) {
      container.removeAttribute('data-dragging');
    }

    // Calculate if we should snap to next/previous slide
    if (wasDragging && this.#drag.progress !== undefined) {
      const progress = Math.abs(this.#drag.progress);
      const direction = this.#drag.progress < 0 ? -1 : 1;
      
      if (progress >= DRAG_COMPLETE_THRESHOLD) {
        // Snap to next or previous slide
        const targetIndex = Math.max(0, Math.min((this.refs.panels?.length ?? 1) - 1, this.#active + direction));
        this.#scrollToSlide(targetIndex, panelsContainer);
      } else {
        // Snap back to current slide
        this.#scrollToSlide(this.#active, panelsContainer);
      }
    }

    this.#drag = null;
  }

  /**
   * @param {MouseEvent | TouchEvent} event
   * @returns {number | null}
   */
  #getClientX(event) {
    if (event instanceof TouchEvent) {
      return event.touches.length > 0 ? event.touches[0].clientX : 
             event.changedTouches.length > 0 ? event.changedTouches[0].clientX : null;
    }
    return event.clientX;
  }

  /**
   * Scroll to a specific slide by index
   * @param {number} index
   * @param {HTMLElement} panelsContainer
   * @param {boolean} [instant]
   */
  #scrollToSlide(index, panelsContainer, instant = false) {
    const { panels } = this.refs;
    if (!panels || index < 0 || index >= panels.length) return;

    const targetPanel = panels[index];
    if (!targetPanel) return;

    // Calculate scroll position using scrollIntoView logic but only for the container
    const containerRect = panelsContainer.getBoundingClientRect();
    const panelRect = targetPanel.getBoundingClientRect();
    const currentScroll = panelsContainer.scrollLeft;
    
    // Calculate the position where panel should be centered
    const panelLeft = panelRect.left - containerRect.left + currentScroll;
    const panelWidth = panelRect.width;
    const containerWidth = panelsContainer.clientWidth;
    const targetScroll = panelLeft - (containerWidth / 2) + (panelWidth / 2);
    
    // Clamp to valid scroll range
    const maxScroll = panelsContainer.scrollWidth - containerWidth;
    const clampedScroll = Math.max(0, Math.min(maxScroll, targetScroll));

    panelsContainer.scrollTo({
      left: clampedScroll,
      behavior: instant ? 'auto' : 'smooth',
    });
  }


  /**
   * @param {AddEventListenerOptions & { signal: AbortSignal }} opts
   */
  #setupPanelFocusManagement(opts) {
    const { panels } = this.refs;
    if (!panels) return;

    for (const [index, panel] of panels.entries()) {
      panel.addEventListener('keydown', (event) => this.#handlePanelKeydown(event, index), opts);
    }
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #handlePanelKeydown(event, index) {
    if (event.key !== 'Tab') return;

    const { panels } = this.refs;
    const panel = /** @type {HTMLElement} */ (event.currentTarget);
    const focusable = this.#getFocusableElements(panel);
    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (event.shiftKey) {
      const isAtStart =
        (firstFocusable && document.activeElement === firstFocusable) ||
        (!focusable.length && document.activeElement === panel);
      if (isAtStart && index > 0) {
        event.preventDefault();
        this.#activate(index - 1);
        this.#focusPanelEdge(index - 1, 'end');
      }
      return;
    }

    const isAtEnd =
      (lastFocusable && document.activeElement === lastFocusable) ||
      (!focusable.length && document.activeElement === panel);

    if (isAtEnd && panels && index < panels.length - 1) {
      event.preventDefault();
      this.#activate(index + 1);
      this.#focusPanelEdge(index + 1, 'start');
    }
  }

  /**
   * @param {number} index
   * @param {'start' | 'end'} [position]
   */
  #focusPanelEdge(index, position = 'start') {
    const panel = this.refs.panels?.[index];
    if (!panel) return;

    const focusable = this.#getFocusableElements(panel);
    const target = position === 'end' ? focusable[focusable.length - 1] : focusable[0];

    requestAnimationFrame(() => (target ?? panel).focus());
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement[]}
   */
  #getFocusableElements(panel) {
    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => !el.closest('[inert]'))
      .map((el) => /** @type {HTMLElement} */ (el));
  }


  #handleMediaQueryChange = () => {
    const wasMobile = this.#isMobile;
    this.#isMobile = isMobileBreakpoint();

    if (wasMobile !== this.#isMobile) {
      this.#clearHeightStyles();
      this.#observeContentHeight();
      this.#updateActiveTab();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#heightObserver?.disconnect();
    this.#heightObserver = null;
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#dragRafId !== null) {
      cancelAnimationFrame(this.#dragRafId);
      this.#dragRafId = null;
    }
    // Clean up global mouse listeners
    if (this.#mouseMoveHandler && this.#mouseUpHandler) {
      document.removeEventListener('mousemove', this.#mouseMoveHandler);
      document.removeEventListener('mouseup', this.#mouseUpHandler);
    }
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Public method to select a slide by index
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, { instant = false } = {}) {
    this.#activate(index, instant);
  }

  /**
   * @param {number} index
   * @param {boolean} [instant]
   */
  #activate(index, instant = false) {
    const { container, tabs } = this.refs;
    if (!tabs || index === this.#active || index < 0 || index >= tabs.length) return;

    if (instant) {
      container.setAttribute('data-instant-transitions', '');
    }

    this.#active = index;
    
    // Scroll to the activated slide
    const panelsContainer = container?.querySelector('.walloffame__panels');
    if (panelsContainer) {
      this.#scrollToSlide(index, panelsContainer, instant);
    }

    this.#updateActiveTab();

    if (instant) {
      // Double rAF to ensure layout is fully settled before re-enabling transitions
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.removeAttribute('data-instant-transitions');
        });
      });
    }
  }

  #updateActiveTab() {
    const { panels } = this.refs;

    for (const [i, panel] of panels?.entries() ?? []) {
      // All panels are visible in side-by-side layout, but only active video plays
      const video = panel.querySelector('video');
      if (video) {
        const isActive = i === this.#active;
        isActive ? video.play() : video.pause();
      }
    }

    this.#updateProgressBar();
  }

  #updateProgressBar() {
    const { panels } = this.refs;
    if (!panels) return;

    // Find progress bar fill outside the component (in the section)
    const section = this.closest('.walloffame-section');
    if (!section) return;

    const progressBarFill = section.querySelector('.walloffame__progress-bar-fill');
    if (!progressBarFill) return;

    const totalSlides = panels.length;
    const currentSlide = this.#active + 1; // 1-based index

    // Calculate position as percentage (0% = left, 100% = right)
    // For single slide, center it
    if (totalSlides === 1) {
      progressBarFill.style.left = '50%';
      progressBarFill.style.transform = 'translateX(-50%)';
      progressBarFill.removeAttribute('data-position');
      return;
    }

    // For multiple slides, calculate position based on current slide
    // Position ranges from 0% (first slide) to 100% (last slide)
    const positionPercent = totalSlides > 1 
      ? ((currentSlide - 1) / (totalSlides - 1)) * 100 
      : 50;

    // Set the position using CSS custom property
    progressBarFill.style.setProperty('--progress-position', `${positionPercent}%`);
    progressBarFill.setAttribute('data-position', 'dynamic');
  }



  #observeContentHeight() {
    const { panels } = this.refs;

    this.#heightObserver?.disconnect();
    this.#heightObserver = new ResizeObserver(() => this.#syncHeight());

    this.#contentObserver?.disconnect();
    this.#contentObserver = new MutationObserver(() => this.#syncHeight());

    for (const panel of panels ?? []) {
      const content = panel.querySelector('.walloffame__content');
      const inner = content?.querySelector('.group-block-content');

      // Observe all relevant elements for resize
      if (inner) this.#heightObserver.observe(inner);
      if (content) this.#heightObserver.observe(content);

      // Observe content for DOM mutations (new blocks, text changes)
      const target = inner ?? content;
      if (target) {
        this.#contentObserver.observe(target, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    }

    this.#syncHeight();
  }

  #syncHeight() {
    const { container } = this.refs;
    const contentHeight = this.#getMaxContentHeight();
    const isAuto = container.getAttribute('size') === 'auto';

    if (this.#isMobile) {
      this.#syncMobileHeight(contentHeight, isAuto);
    } else {
      this.#syncDesktopHeight(contentHeight, isAuto);
    }
  }

  /**
   * @param {number} contentHeight
   * @param {boolean} isAuto
   */
  #syncDesktopHeight(contentHeight, isAuto) {
    const { container, panels } = this.refs;
    if (!panels) return;

    // Auto height - let content determine height
    // Clear any fixed heights to allow auto sizing
    container.style.height = '';
    
    // Find panels container
    const panelsContainer = container.querySelector('.walloffame__panels');
    if (panelsContainer) {
      panelsContainer.style.height = '';
    }

    // Clear height on all panels to allow auto sizing
    for (const panel of panels) {
      panel.style.height = '';
      const panelContent = panel.querySelector('.walloffame__panel-content');
      if (panelContent) {
        panelContent.style.height = '';
      }
    }
  }

  /**
   * @param {number} contentHeight
   * @param {boolean} isAuto
   */
  #syncMobileHeight(contentHeight, isAuto) {
    const { container, panels } = this.refs;
    if (!container || !panels) return;

    // Auto height - let content determine height
    // Clear any fixed heights to allow auto sizing
    container.style.height = '';
    
    // Find panels container
    const panelsContainer = container.querySelector('.walloffame__panels');
    if (panelsContainer) {
      panelsContainer.style.height = '';
    }

    // Clear height on all panels to allow auto sizing
    for (const panel of panels) {
      panel.style.height = '';
      const panelContent = panel.querySelector('.walloffame__panel-content');
      if (panelContent) {
        panelContent.style.height = '';
      }
    }
  }

  #clearHeightStyles() {
    const { container, panels } = this.refs;

    this.style.minHeight = '';
    container.style.height = '';
    container.style.removeProperty('--active-panel-height');
    
    // Clear panel heights
    if (panels) {
      for (const panel of panels) {
        const panelContent = panel.querySelector('.walloffame__panel-content');
        if (panelContent) {
          panelContent.style.height = '';
        }
      }
    }
  }

  #getMaxContentHeight() {
    const { panels } = this.refs;
    let max = 0;

    for (const panel of panels ?? []) {
      const content = panel.querySelector('.walloffame__content');
      if (!content) continue;

      const inner = /** @type {HTMLElement} */ (content.querySelector('.group-block-content') ?? content);

      // Temporarily set height to auto for accurate measurement
      // This is needed because height: 100% collapses when parent has no height
      const savedHeight = inner.style.height;
      inner.style.height = 'auto';

      const styles = getComputedStyle(content);
      const paddingTop = parseFloat(styles.paddingBlockStart || styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBlockEnd || styles.paddingBottom) || 0;

      const height = (inner.scrollHeight || 0) + paddingTop + paddingBottom;
      if (height > max) max = height;

      // Restore original height
      inner.style.height = savedHeight;
    }

    return max;
  }
}

customElements.define('walloffame-component', WalloffameComponent);

